<template>
  <v-slide-y-transition>
    <v-container
      v-show="showDialog"
      :key="showDialog"
      :aria-hidden="showDialog"
      class="px-2"
    >
      <div
        :class="[!fullWidth ? 'col-md-6 col-lg-8' : '', 'col-12', 'px-0']"
        role="alert"
      >
        <v-sheet
          :dark="lightText"
          :light="darkText"
          :color="color"
          :style="border"
          :class="fullWidth ? 'px-4 py-2' : 'px-4 py-4'"
        >
          <div>
            <h2 class="mb-4 d-flex justify-space-between">
              <div>
                <v-icon class="mr-2" color="red">{{ icon }}</v-icon>
                <span style="vertical-align: sub;">
                  {{ title }}
                </span>
              </div>

              <div class="text-right">
                <v-btn
                  text
                  :aria-label="$t('common:closeButton.alt', 'Close')"
                  @click="close"
                >
                  <v-icon class="align-self-baseline"
                    >$iconTimesCircle</v-icon
                  >
                </v-btn>
              </div>
            </h2>

            <div style="white-space: pre-line;">{{ text }}</div>

            <ul>
              <li v-for="(item, i) in displayMessages" :key="i">
                <template v-if="typeof item === 'string'">
                  {{ item }}
                </template>

                {{ item.fieldName }}
                <ul>
                  <li v-for="(err, subI) in item.errors" :key="subI">
                    {{ err }}
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </v-sheet>
      </div>
    </v-container>
  </v-slide-y-transition>
</template>
<script>
import { defineComponent } from "vue";
import { compact } from "lodash-es";
import { useDisplayText } from "@composables/display_text";
export default defineComponent({
  name: "AppShowFeedback",
  props: {
    fullWidth: {
      type: Boolean,
      default: false
    },
    inDialog: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { displayText } = useDisplayText();
    return { displayText };
  },
  data() {
    return {
      showDialog: false,
      messages: null,
      darkText: null,
      lightText: null,
      title: null,
      icon: null,
      color: null,
      border: null,
      text: null
    };
  },
  computed: {
    displayMessages: {
      get() {
        if (this.messages) {
          return this.messages;
        } else {
          return [];
        }
      },
      set(val) {
        if (!val) {
          this.messages = null;
          return;
        }
        if (val.constructor === Object) {
          const errorFields = Object.keys(val);
          const errorMessages = errorFields.map(fieldName => {
            let { errors, i18nKey, translatedFieldName } = val[fieldName];
            if (!errors) errors = val[fieldName];
            let fieldNameI18n = null;

            if (translatedFieldName) fieldNameI18n = translatedFieldName;
            else if (i18nKey) {
              fieldNameI18n = this.$t(i18nKey, fieldName);
            } else {
              fieldNameI18n =
                fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
            }

            if (errors.length <= 0) return;
            else if (errors.length == 1) {
              return `${fieldNameI18n} ${errors[0].charAt(0).toLowerCase() +
                errors[0].slice(1)}`;
            } else {
              return {
                fieldName: fieldNameI18n,
                errors: errors
              };
            }
          });

          this.messages = compact(errorMessages);
        } else {
          this.messages = val;
        }
      }
    },
    i18nMessages() {
      return [
        "calendar:",
        "common:",
        "events:",
        "horses:",
        "navigation:",
        "organisations:",
        "people:",
        "server:",
        "signin:",
        "support:",
        "temporary:",
        "wallet:",
        "server:"
      ];
    }
  },
  watch: {
    $route() {
      this.close();
    }
  },
  mounted() {
    this.close();
  },
  methods: {
    open(
      type,
      options = { messages: [], title: null, icon: null, text: null }
    ) {
      const { messages, title, icon, text } = options;
      this.text = text;
      switch (type) {
        case "success":
          if (!title)
            this.title = this.$t("common:successMessage.heading", "Success");
          if (!icon) this.icon = "$iconCircleCheck";
          this.color = "primary-light";
          break;
        case "warn":
          if (!title)
            this.title = this.$t("common:warningMessage.heading", "Warning");
          if (!icon) this.icon = "$iconBellRinged";
          this.color = "alert";
          if (!text) this.text = this.$t("common:reviewInput.text");
          break;
        case "error":
          if (!title)
            this.title = this.$t("common:errorMessage.heading", "Error");
          if (!icon) this.icon = "$iconExclamationTriangle";
          this.color = "alert";

          break;
        default:
          if (!title)
            this.title = this.$t("common:infoMessage.heading", "Info");
          if (!icon) this.icon = "$iconBell";
          this.color = "primary-light";
      }

      this.border = "box-shadow: 0px 0px 2px rgb(89, 89, 89);";
      this.displayMessages = messages;

      this.showDialog = true;

      window.requestAnimationFrame(() =>
        this.$el.scrollIntoView({ block: "start" })
      );
    },
    showGraphqlError(errs) {
      if (errs) {
        if (typeof errs.constructor == Array) {
          this.open("error", {
            text: "",
            messages: errs.map(message => {
              if (message.key) {
                return this.$t(message.key, message.msg);
              } else {
                return message.msg || message.message;
              }
            })
          });
        } else if (errs.graphQLErrors && errs.graphQLErrors.length > 0) {
          const error = errs.graphQLErrors[0];
          let messages =
            error.messages && error.messages.constructor === String
              ? [error.messages]
              : error.messages;

          this.open("error", {
            text: error.key ? this.$t(error.key, error.msg) : error.message,
            messages: this.handledMessages(messages)
          });
        } else if (errs.constructor === String || errs.constructor === Error) {
          this.open("error", {
            text: this.i18nMessages.some(namespace => errs.includes(namespace))
              ? this.$t(errs, errs)
              : errs,
            messages: null
          });
        } else {
          this.open("error", {
            text: "Undefined error!",
            messages: null
          });
        }
      } else {
        this.open("error", {
          text: "Undefined error!",
          messages: null
        });
      }
    },
    handledMessages(messages) {
      if (Array.isArray(messages)) {
        return messages.map(message => {
          if (typeof message === "string") {
            if (
              this.i18nMessages.some(namespace => message.includes(namespace))
            ) {
              message = this.$t(message, message);
            }
            return message;
          }
          if (message.key) {
            return this.$t(message.key, message.msg);
          } else {
            if (message.translation_key || message.translationKey) {
              return this.displayText(message);
            } else {
              let txt = message.msg || message.message;
              if (txt.includes("GraphQL error:")) {
                txt = txt.split("GraphQL error:").join(" ");
              }
              return txt;
            }
          }
        });
      } else {
        return messages;
      }
    },
    close() {
      this.showDialog = false;
      this.displayMessages = null;
      this.text = null;
    }
  }
});
</script>
